import React, { useState, useEffect } from "react";
import { 
  getMeusModelosMedicamentos, 
  adicionarModeloMedicamento,
  atualizarModeloMedicamento
} from "../../services/authService";
import { useNotifications } from '../../context/NotificationContext';
import { Link } from "react-router-dom";
import "./AdicionarMedicamento.scss";

const AdicionarMedicamento = () => {
  const [formData, setFormData] = useState({
    nome: "",
    nomeModelo: "",
    descricao: "",
    fabricante: "",
    dosagem: "",
    unidadeDosagem: "comprimido",
    frequencia: "",
    horarios: "",
    duracaoTratamento: "",
    unidadeDuracao: "dias",
    viaAdministracao: "oral",
    efeitosColaterais: "",
    observacoes: "",
    estoqueMinimo: "",
    categoria: "analgésico"
  });
  const [mensagem, setMensagem] = useState("");
  const [modelos, setModelos] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [modoEdicao, setModoEdicao] = useState(false);
  const [modeloEditando, setModeloEditando] = useState(null);
  const { addNotification } = useNotifications();
  
  const categoriasOpcoes = [
    "analgésico", "antibiótico", "anti-inflamatório", "antialérgico", 
    "antidepressivo", "cardiovascular", "hormônio", "vitamina", "suplemento", "outro"
  ];
  
  const unidadesDosagem = [
    "comprimido", "cápsula", "ml", "mg", "g", "gota", "ampola", "unidade"
  ];
  
  const unidadesDuracao = [
    "dias", "semanas", "meses", "anos", "contínuo"
  ];
  
  const viasAdministracao = [
    "oral", "sublingual", "tópica", "intravenosa", "intramuscular", 
    "subcutânea", "inalatória", "ocular", "retal"
  ];

  useEffect(() => {
    // Verificar se há parâmetros na URL para edição
    const params = new URLSearchParams(window.location.search);
    const modeloId = params.get('editar');
    
    if (modeloId) {
      carregarModeloParaEdicao(modeloId);
    }
  }, []);

  const carregarModeloParaEdicao = async (modeloId) => {
    try {
      setCarregando(true);
      const data = await getMeusModelosMedicamentos();
      const modelo = data.find(m => m.id.toString() === modeloId);
      
      if (modelo) {
        handleEditarModelo(modelo);
      } else {
        setMensagem("Modelo não encontrado.");
      }
    } catch (error) {
      console.error("Erro ao carregar modelo:", error);
      setMensagem("Erro ao carregar modelo: " + (error.message || "Erro desconhecido"));
    } finally {
      setCarregando(false);
    }
  };

  const handleEditarModelo = (modelo) => {
    setFormData({
      nome: modelo.nome || "",
      nomeModelo: modelo.nome || "",
      descricao: modelo.descricao || "",
      fabricante: modelo.fabricante || "",
      dosagem: modelo.dosagem.split(' ')[0] || "",
      unidadeDosagem: modelo.dosagem.split(' ')[1] || "comprimido",
      frequencia: modelo.frequencia || "",
      horarios: modelo.horarios || "",
      duracaoTratamento: modelo.duracaoTratamento?.split(' ')[0] || "",
      unidadeDuracao: modelo.duracaoTratamento?.split(' ')[1] || "dias",
      viaAdministracao: modelo.viaAdministracao || "oral",
      efeitosColaterais: modelo.efeitosColaterais || "",
      observacoes: modelo.observacoes || "",
      estoqueMinimo: modelo.estoqueMinimo || "",
      categoria: modelo.categoria || "analgésico"
    });
    setModeloEditando(modelo);
    setModoEdicao(true);
  };

  const limparCampos = () => {
    setFormData({
      nome: "",
      nomeModelo: "",
      descricao: "",
      fabricante: "",
      dosagem: "",
      unidadeDosagem: "comprimido",
      frequencia: "",
      horarios: "",
      duracaoTratamento: "",
      unidadeDuracao: "dias",
      viaAdministracao: "oral",
      efeitosColaterais: "",
      observacoes: "",
      estoqueMinimo: "",
      categoria: "analgésico"
    });
    setModeloEditando(null);
    setModoEdicao(false);
    
    // Limpar parâmetro da URL se estiver em modo de edição
    if (window.location.search.includes('editar')) {
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!formData.nomeModelo || !formData.nome || !formData.dosagem || !formData.frequencia) {
      setMensagem("Preencha todos os campos obrigatórios!");
      return;
    }
    
    try {
      const modelo = {
        nome: formData.nomeModelo,
        descricao: formData.descricao,
        fabricante: formData.fabricante,
        dosagem: `${formData.dosagem} ${formData.unidadeDosagem}`,
        frequencia: formData.frequencia,
        horarios: formData.horarios,
        duracaoTratamento: formData.duracaoTratamento ? `${formData.duracaoTratamento} ${formData.unidadeDuracao}` : "",
        viaAdministracao: formData.viaAdministracao,
        efeitosColaterais: formData.efeitosColaterais,
        observacoes: formData.observacoes,
        estoqueMinimo: formData.estoqueMinimo,
        categoria: formData.categoria
      };

      if (modoEdicao && modeloEditando) {
        await atualizarModeloMedicamento(modeloEditando.id, modelo);
        setMensagem("Modelo de medicamento atualizado com sucesso!");
        
        // Adicionar notificação
        addNotification({
          title: "Modelo atualizado",
          message: `O modelo ${modelo.nome} foi atualizado com sucesso`,
          type: "success"
        });
      } else {
        await adicionarModeloMedicamento(modelo);
        setMensagem("Modelo de medicamento salvo com sucesso!");
        
        // Adicionar notificação
        addNotification({
          title: "Modelo adicionado",
          message: `O modelo ${modelo.nome} foi adicionado com sucesso`,
          type: "success"
        });
      }
      
      limparCampos();
      
      // Rolar para o topo da página
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      setMensagem("Erro ao salvar modelo: " + (error.message || "Erro desconhecido"));
    }
  };

  return (
    <div className="adicionar-medicamento-wrapper">
      <div className="adicionar-medicamento-container">
        <div className="page-header">
          <h2>{modoEdicao ? "Editar Modelo de Medicamento" : "Novo Modelo de Medicamento"}</h2>
          <p className="page-description">
            Crie modelos de medicamentos para facilitar o registro de medicamentos na sua caixa.
            <br />
            <Link to="/relatorio" className="ver-catalogo-link">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                <line x1="9" y1="3" x2="9" y2="21"></line>
              </svg>
              Ver catálogo completo de modelos
            </Link>
          </p>
        </div>
        
        {mensagem && (
          <div className={`mensagem ${mensagem.includes("sucesso") ? "success" : "error"}`}>
            <div className="mensagem-content">
              {mensagem.includes("sucesso") ? (
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                  <polyline points="22 4 12 14.01 9 11.01"></polyline>
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="8" x2="12" y2="12"></line>
                  <line x1="12" y1="16" x2="12.01" y2="16"></line>
                </svg>
              )}
              <span>{mensagem}</span>
            </div>
            <button 
              className="close-message" 
              onClick={() => setMensagem("")}
              aria-label="Fechar mensagem"
            >
              ×
            </button>
          </div>
        )}
        
        {carregando ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Carregando modelo...</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="medicamento-form">
            <div className="form-header">
              <h3>{modoEdicao ? "Editar Modelo" : "Novo Modelo de Medicamento"}</h3>
              {modoEdicao && (
                <button 
                  type="button" 
                  className="btn-cancel-icon"
                  onClick={limparCampos}
                  title="Cancelar edição"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
              )}
            </div>
            
            <div className="form-section">
              <h4>
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                  <circle cx="9" cy="7" r="4"></circle>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                  <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                </svg>
                Informações Básicas
              </h4>
              <div className="form-row">
                <div className="form-group">
                  <label>Nome do Modelo</label>
                  <input
                    type="text"
                    name="nomeModelo"
                    value={formData.nomeModelo}
                    onChange={handleChange}
                    placeholder="Ex: Paracetamol 500mg"
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label>Categoria</label>
                  <select 
                    name="categoria"
                    value={formData.categoria}
                    onChange={handleChange}
                  >
                    {categoriasOpcoes.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Nome do Medicamento</label>
                  <input
                    type="text"
                    name="nome"
                    value={formData.nome}
                    onChange={handleChange}
                    placeholder="Ex: Paracetamol"
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label>Fabricante</label>
                  <input
                    type="text"
                    name="fabricante"
                    value={formData.fabricante}
                    onChange={handleChange}
                    placeholder="Ex: Medley"
                  />
                </div>
              </div>
            </div>
            
            <div className="form-section">
              <h4>
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="m12 8-9.04 9.06a2.82 2.82 0 1 0 3.98 3.98L16 12"></path>
                  <circle cx="17" cy="7" r="5"></circle>
                </svg>
                Posologia
              </h4>
              <div className="form-row">
                <div className="form-group">
                  <label>Dosagem</label>
                  <div className="input-with-unit">
                    <input
                      type="text"
                      name="dosagem"
                      value={formData.dosagem}
                      onChange={handleChange}
                      placeholder="Ex: 500"
                      required
                    />
                    <select 
                      name="unidadeDosagem"
                      value={formData.unidadeDosagem}
                      onChange={handleChange}
                    >
                      {unidadesDosagem.map(unid => (
                        <option key={unid} value={unid}>{unid}</option>
                      ))}
                    </select>
                  </div>
                </div>
                
                <div className="form-group">
                  <label>Via de Administração</label>
                  <select 
                    name="viaAdministracao"
                    value={formData.viaAdministracao}
                    onChange={handleChange}
                  >
                    {viasAdministracao.map(via => (
                      <option key={via} value={via}>{via}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Frequência</label>
                  <input
                    type="text"
                    name="frequencia"
                    value={formData.frequencia}
                    onChange={handleChange}
                    placeholder="Ex: A cada 8 horas"
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label>Horários Recomendados</label>
                  <input
                    type="text"
                    name="horarios"
                    value={formData.horarios}
                    onChange={handleChange}
                    placeholder="Ex: 8h, 16h, 0h"
                  />
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Duração do Tratamento</label>
                  <div className="input-with-unit">
                    <input
                      type="text"
                      name="duracaoTratamento"
                      value={formData.duracaoTratamento}
                      onChange={handleChange}
                      placeholder="Ex: 7"
                    />
                    <select 
                      name="unidadeDuracao"
                      value={formData.unidadeDuracao}
                      onChange={handleChange}
                    >
                      {unidadesDuracao.map(unid => (
                        <option key={unid} value={unid}>{unid}</option>
                      ))}
                    </select>
                  </div>
                </div>
                
                <div className="form-group">
                  <label>Estoque Mínimo</label>
                  <input
                    type="number"
                    name="estoqueMinimo"
                    value={formData.estoqueMinimo}
                    onChange={handleChange}
                    placeholder="Ex: 5"
                    min="0"
                  />
                </div>
              </div>
            </div>
            
            <div className="form-section">
              <h4>
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="16" x2="12" y2="12"></line>
                  <line x1="12" y1="8" x2="12.01" y2="8"></line>
                </svg>
                Informações Adicionais
              </h4>
              <div className="form-group">
                <label>Descrição</label>
                <textarea
                  name="descricao"
                  value={formData.descricao}
                  onChange={handleChange}
                  placeholder="Descrição do medicamento"
                  rows="2"
                />
              </div>
              
              <div className="form-group">
                <label>Efeitos Colaterais</label>
                <textarea
                  name="efeitosColaterais"
                  value={formData.efeitosColaterais}
                  onChange={handleChange}
                  placeholder="Liste os possíveis efeitos colaterais"
                  rows="2"
                />
              </div>
              
              <div className="form-group">
                <label>Observações</label>
                <textarea
                  name="observacoes"
                  value={formData.observacoes}
                  onChange={handleChange}
                  placeholder="Observações adicionais (ex: tomar com água, evitar bebidas alcoólicas, etc)"
                  rows="2"
                />
              </div>
            </div>
            
            <div className="buttons-container">
              <button type="submit" className="btn-primary">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>
                  <polyline points="17 21 17 13 7 13 7 21"></polyline>
                  <polyline points="7 3 7 8 15 8"></polyline>
                </svg>
                {modoEdicao ? "Atualizar Modelo" : "Salvar Modelo"}
              </button>
              
              {modoEdicao && (
                <button 
                  type="button" 
                  className="btn-secondary"
                  onClick={limparCampos}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                  Cancelar Edição
                </button>
              )}
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default AdicionarMedicamento;